import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft } from "lucide-react";
import { useState } from "react";
import { Badge, Button, ConfirmDialog, DataTable, Dialog, EmptyState, formatDateTime, formatRelative, KeyValue, Notice, PageHeader, Pagination, Panel, Select, toast, type BadgeTone } from "@hostdock/ui";
import type { AdminMigrationSummary, MigrationStatus } from "@hostdock/types";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";
import { useSession } from "../session";

const PAGE_SIZE = 50;
const TONE: Record<MigrationStatus, BadgeTone> = {
  DRAFT: "neutral", DISCOVERING: "info", DISCOVERED: "accent", QUEUED: "neutral", CONNECTING: "info", EXPORTING: "info", TRANSFERRING: "info", RESTORING: "info", VALIDATING: "info",
  COMPLETED: "success", COMPLETED_WITH_WARNINGS: "warning", FAILED: "danger", CANCELLED: "neutral",
};
const label = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, " ");
const RUNNING: MigrationStatus[] = ["DISCOVERING", "QUEUED", "CONNECTING", "EXPORTING", "TRANSFERRING", "RESTORING", "VALIDATING"];

export function MigrationsPage() {
  return <RequirePermission anyOf={["MIGRATION_VIEW"]}><Migrations /></RequirePermission>;
}

function Migrations() {
  const { can } = useSession();
  const qc = useQueryClient();
  const { values, page, set } = useListParams(["status"] as const);
  const [open, setOpen] = useState<AdminMigrationSummary | null>(null);
  const [cancelling, setCancelling] = useState<AdminMigrationSummary | null>(null);
  const q = useQuery({ queryKey: ["admin-migrations", values, page], queryFn: () => api.migrations.list({ ...values, page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData, refetchInterval: 10_000 });
  const cancel = useMutation({ meta: { inlineError: true },
    mutationFn: (id: string) => api.migrations.cancel(id),
    onSuccess: () => { toast.success("Migration cancelled and credentials deleted"); setCancelling(null); void qc.invalidateQueries({ queryKey: ["admin-migrations"] }); },
  });
  return (
    <>
      <PageHeader title="Migrations" description="Customer migrations from other hosts. Source credentials are never visible here." />
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <Select value={values.status} onChange={(e) => set({ status: e.target.value })} className="w-56" aria-label="Status">
            <option value="">All statuses</option>
            {["QUEUED", "DISCOVERING", "CONNECTING", "RESTORING", "VALIDATING", "COMPLETED", "COMPLETED_WITH_WARNINGS", "FAILED", "CANCELLED"].map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(m) => m.id}
          onRowClick={setOpen}
          empty={<EmptyState icon={ArrowRightLeft} title={values.status ? "No matching migrations" : "No migrations yet"} description="Customers start migrations from Migrate to HostDock in their panel." />}
          columns={[
            { key: "c", header: "Customer", cell: (m) => <span className="font-medium">{m.customer.name}</span> },
            { key: "s", header: "Source", cell: (m) => <div><p>{m.sourceHost}</p><p className="text-[12px] text-muted">{m.providerLabel}</p></div> },
            { key: "st", header: "Status", cell: (m) => <Badge tone={TONE[m.status]} dot>{label(m.status)}</Badge> },
            { key: "p", header: "Progress", cell: (m) => <span className="tabular-nums text-muted">{m.progress}%</span>, hideOnMobile: true },
            { key: "cr", header: "Credentials", cell: (m) => (m.credentialsStored ? <Badge tone="warning">Held</Badge> : <Badge tone="neutral">Deleted</Badge>), hideOnMobile: true },
            { key: "t", header: "Started", cell: (m) => <span className="text-muted">{formatRelative(m.createdAt)}</span> },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
      {open && <MigrationDialog summary={open} onClose={() => setOpen(null)} onCancel={can("MIGRATION_MANAGE") && !["COMPLETED", "COMPLETED_WITH_WARNINGS", "FAILED", "CANCELLED"].includes(open.status) ? () => { setCancelling(open); setOpen(null); } : undefined} />}
      <ConfirmDialog open={cancelling !== null} onOpenChange={(o) => !o && setCancelling(null)} title="Cancel this migration?" description={`This stops ${cancelling?.customer.name}'s migration and deletes the stored source credentials.`} confirmLabel="Cancel migration" loading={cancel.isPending} error={cancel.error} onConfirm={() => cancelling && cancel.mutate(cancelling.id)} />
    </>
  );
}

function MigrationDialog({ summary, onClose, onCancel }: { summary: AdminMigrationSummary; onClose(): void; onCancel?: () => void }) {
  const q = useQuery({ queryKey: ["admin-migration", summary.id], queryFn: () => api.migrations.get(summary.id), refetchInterval: RUNNING.includes(summary.status) ? 3000 : false });
  const m = q.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`${summary.customer.name} · ${summary.providerLabel}`} description={summary.sourceHost} size="lg" footer={<>{onCancel && <Button variant="danger" onClick={onCancel}>Cancel migration</Button>}<Button onClick={onClose}>Close</Button></>}>
      <div className="flex flex-col gap-4">
        <KeyValue items={[
          { label: "Status", value: label(summary.status) }, { label: "Message", value: summary.statusMessage ?? "—" }, { label: "Account", value: summary.usernameHint },
          { label: "Destination", value: summary.targetWebsite?.domain ?? "—" }, { label: "Started", value: formatDateTime(summary.startedAt) }, { label: "Finished", value: formatDateTime(summary.finishedAt) },
        ]} />
        {m?.warnings.map((w) => <Notice key={w} tone="warning">{w}</Notice>)}
        {m && (
          <>
            <ul className="divide-y divide-line rounded-md border border-line text-[13px]">
              {m.resources.filter((r) => r.selected || r.status === "FAILED").map((r) => <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-1.5"><span className="min-w-0 truncate"><span className="text-muted">{r.kind}: </span>{r.name}{r.error && <span className="block text-[12px] text-danger">{r.error}</span>}</span><span className="text-muted">{label(r.status)}</span></li>)}
              {!m.resources.some((r) => r.selected) && <li className="px-3 py-2 text-muted">Nothing selected yet.</li>}
            </ul>
            <ol className="max-h-56 space-y-1 overflow-y-auto font-mono text-[12px]">
              {m.logs.map((l) => <li key={l.id} className={l.level === "ERROR" ? "text-danger" : l.level === "WARN" ? "text-warning" : "text-muted"}>{formatDateTime(l.at)} [{l.step}] {l.message}</li>)}
            </ol>
          </>
        )}
      </div>
    </Dialog>
  );
}
